import { AevocamSprite } from './AevocamSprite'
import { AevocamHighRes } from './AevocamHighRes'
import { useAevocamPlayer } from './useAevocamPlayer'
import './AevocamViewer.css'

/**
 * Feed data structure from the API
 */
export type Feed = {
  name: string
  timezone: string
  schedule: any
  createdAt: string
  disabled: boolean
  deletedAt: string | null
}

/**
 * Photo data structure from the API
 */
export type Photo = {
  /** Day in YYYY-MM-DD format */
  d: string
  /** Time slot packed into an integer as HHMMSS */
  t: number
  /** Sequence number */
  s: number
  /** Width in pixels */
  w: number
  /** Height in pixels */
  h: number
  /** Sprite sheet sequence */
  slq: number | null
  /** Sprite sheet scale */
  slc: number | null
  /** Sprite sheet slot */
  sll: number | null
  /** Sprite sheet sequence */
  smq: number | null
  /** Sprite sheet scale */
  smc: number | null
  /** Sprite sheet slot */
  sml: number | null
  /** True when manifest was fetched with markOutliers and this photo is an outlier */
  outlier?: boolean
}

/**
 * Sprite sheet data structure from the API
 */
export type SpriteSheet = {
  /** Sequence number */
  sq: number
  /** Scale */
  sc: number
  /** Checksum */
  ss: number | null
  /** Grid size (e.g., 8 for an 8x8 grid) */
  sg: number
  /** Width in pixels */
  w: number
  /** Height in pixels */
  h: number
}

/** Renders the layered sprite / high-res stack. Shift+wheel zoom is handled on `AevocamViewport` (this subtree uses `pointer-events: none`). */
export function AevocamViewer() {
  const {
    viewerPipelineStage,
    cdnToken,
    viewerPhotos: photos,
    spriteSheetsLow,
    spriteSheetsMedium,
    spritesHighGearLow,
    spritesHighGearMedium,
    viewerViewWidth: viewWidth,
    viewerViewHeight: viewHeight,
    actions: { setViewerLowSpriteGear, setViewerMediumSpriteGear },
  } = useAevocamPlayer()

  if (viewerPipelineStage === 0 || !cdnToken) {
    return <div>Loading...</div>
  }

  if (viewerPipelineStage >= 2 && photos.length === 0) {
    return (
      <div className="AevocamViewer Empty">
        <div>No photos</div>
      </div>
    )
  }

  return (
    <div
      className="AevocamViewer"
      style={{
        width: viewWidth,
        height: viewHeight,
      }}
    >
      <AevocamSprite
        photos={photos}
        spriteSheets={spriteSheetsLow}
        setSpritesHighGear={setViewerLowSpriteGear}
        viewWidth={viewWidth}
        viewHeight={viewHeight}
        spriteSet="low"
        highGear
        token={cdnToken}
      />
      {viewerPipelineStage >= 2 && (
        <>
          <AevocamSprite
            photos={photos}
            spriteSheets={spriteSheetsMedium}
            setSpritesHighGear={setViewerMediumSpriteGear}
            viewWidth={viewWidth}
            viewHeight={viewHeight}
            spriteSet="medium"
            highGear={!spritesHighGearLow}
            token={cdnToken}
          />
          <AevocamHighRes
            photos={photos}
            viewWidth={viewWidth}
            viewHeight={viewHeight}
            highGear={!spritesHighGearLow && !spritesHighGearMedium}
            token={cdnToken}
          />
        </>
      )}
    </div>
  )
}
